import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Software dev isn't just a logical process",
  "description": "",
  "published": "2011-08-10T00:00:00.000Z",
  "redirect_from": ["/blog/software-dev-isnt-just-a-logical-process/swizec/2108"],
  "categories": "Companies, development, Java, Programmer, Programming, Software developer, Software development, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/08/300px-Mito_Art_Tower3.jpg",
        "alt": "Mito Art Tower by Arata Isozaki",
        "title": "Mito Art Tower by Arata Isozaki"
      }}></img></p>
    <p>{`A very cool post by `}<a parentName="p" {...{
        "href": "http://jasonrudolph.com/"
      }}>{`Jason Rudolph`}</a>{` entitled `}<a parentName="p" {...{
        "href": "http://jasonrudolph.com/blog/2011/08/09/programming-achievements-how-to-level-up-as-a-developer/"
      }}>{`How to Level Up As Developer `}</a>{`made the rounds around the internet today. The basic premise was that defining progress for `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Programmer",
        "title": "Programmer"
      }}>{`programmers`}</a>{` and/or developers is a very difficult thing. Many good points in there, but I have a small bone to pick with this part:`}</p>
    <blockquote>
      <p parentName="blockquote">{`And as `}<strong parentName="p">{`logically-minded developer types`}</strong>{`, the lack of a well-defined route can make for a daunting journey from novice to master.`}</p>
    </blockquote>
    <p>{`This perception of developers is something I've noticed a lot pretty much everywhere. People almost invariably think of developers as these logical people who like to keep things orderly, always categorize everything and are generally displeased with things that aren't very engineering-like.`}</p>
    <p>{`It sucks!`}</p>
    <p>{`That kind of perception paints programmers as boring people, some kind of real-world Vulcan almost.`}</p>
    <p>{`Granted, this might be true of the sort of programmer who uses Java with love, works in a cubicle farm somewhere and simply has never been exposed to the true joy of programming. The artistic side of it all. (and I feel very sorry for them indeed)`}</p>
    <p>{`I've argued in the past that programming is more akin to `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/the-mind-of-a-wandering-software-poet/swizec/59",
        "title": "The mind of a wandering software poet"
      }}>{`writing poetry`}</a>{` than it is to this rigorous discipline we like to call engineering. Some have even likened programming to `}<a parentName="p" {...{
        "href": "http://chrisaitchison.com/2011/05/03/you-are-not-a-software-engineer"
      }}>{`tending a garden`}</a>{`.`}</p>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Art`}</strong>{` is the product or process of deliberately arranging items (often with symbolic significance) in a way that influences and affects one or more of the senses, emotions, and `}<em parentName="p">{`intellect`}</em>{`.`}</p>
    </blockquote>
    <p>{`Wikipedia clearly states that art is about arranging abstract things into something new that makes us feel something, or just makes us think. A very broad definition, but `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Software_development",
        "title": "Software development"
      }}>{`software development`}</a>{` fits the bill perfectly.`}</p>
    <p>{`I'm guessing a lot of people do actually approach software development as a rigorous discipline where you take a problem and chip away at it with the available tools until it is solved. In some cases, like `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Real-time_computing",
        "title": "Real-time computing"
      }}>{`real-time systems`}</a>{`, this is probably a good thing too.`}</p>
    <p>{`For me, that approach has never really worked. I like to take a more artistic approach to solving hard architecture and algorithmic problems.`}</p>
    <p><em parentName="p">{`Inspiration`}</em>{`.`}</p>
    <p>{`Inspiration is key.`}</p>
    <p>{`I find that writing code from a more inspired perspective produces results that are much more beautiful to behold, that work better, sometimes even work faster, and are most of all more pleasurable to come up with. It sometimes even lets me come up with solutions completely `}<em parentName="p"><a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/Lateral_thinking"
        }}>{`unachievable through a series of logical steps`}</a></em>{`.`}</p>
    <p>{`A lot of great ideas come to people when they are busy doing something else and just let their mind wander. This is why many claim that they get the best ideas under the shower, or on the crapper, a certain man was also known to get a lot of ideas while pushing papers as a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Patent_examiner",
        "title": "Patent examiner"
      }}>{`patent clerk`}</a>{` ... it's all about disengaging yourself from the problem, thinking about it somewhere in the back of your head and waiting for inspiration to strike.`}</p>
    <p>{`Thing is, by lumping programmers into this `}<em parentName="p">{`Logical People (tm)`}</em>{` group and thinking of software development as a `}<em parentName="p">{`Logical Process (tm)`}</em>{` you are actually making a great disservice to pretty much everyone. When developing software we aren't just solving a problem through a series of logical steps.`}</p>
    <p>{`We are `}<em parentName="p">{`creating solutions`}</em>{`!`}</p>
    <p>{`Software development is an inherently creative process and I think it should be considered as such. Perhaps then there would be more beautiful code in the world. There would be more happy developers. And the world would be a better place to be as a result.`}</p>
    <p>{`If software development was seen as a more interesting funtastic field, perhaps we wouldn't be seeing declining freshman enrollment into studying what is essentially the most important industry of our time since it runs pretty much everything.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://abdulapopoola.wordpress.com/2011/07/31/become-a-better-programmer/"
        }}>{`Become a better programmer`}</a>{` (abdulapopoola.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://thinkup.waldenu.edu/career-development/career-planning/item/11415-education-needed-computer-programming&sa=U&ei=h5ZkTbjRM4yTtwex-ozwBg&ved=0CN0BEBYwJjisAg&usg=AFQjCNGTAXQRMjFouYmpHXq9vPiK_gSBcQ"
        }}>{`Education Needed for Computer Programming`}</a>{` (thinkup.waldenu.edu)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://rc3.org/2011/08/06/the-opportunity-cost-of-a-broad-skillset/"
        }}>{`The opportunity cost of a broad skillset`}</a>{` (rc3.org)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.prweb.com/releases/2009/11/prweb3215144.htm"
        }}>{`Taking the Drudgery Out of Software Development With a New Set of Tools from the ReDSeeDS project`}</a>{` (prweb.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://avdi.org/devblog/2011/08/08/software-superstitions/"
        }}>{`Software Superstitions`}</a>{` (avdi.org)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=61170871-39b3-47f8-9a7b-ff2a1ea89112",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      